/**
 * Assets management page for managing domains, IPs, IP ranges, and URLs.
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getAssets, getAssetCount, createAsset, deleteAsset } from '@/api/assets';
import { coreApiMode } from '@/config/deployment';
import { apiError } from '@/utils/apiError';
import { attachCredential } from '@/api/credentials';
import { useAuth } from '@/hooks/useAuth';
import { useCredentials } from '@/hooks/useCredentials';
import AssetList from '@/components/assets/AssetList';
import AssetForm from '@/components/assets/AssetForm';
import AssetCredentialPanel from '@/components/assets/AssetCredentialPanel';
import AssetVerificationPanel from '@/components/assets/AssetVerificationPanel';
import DiscoveryPanel from '@/components/assets/DiscoveryPanel';
import DeviceUsage from '@/components/assets/DeviceUsage';
import { getMyUsage } from '@/api/tiers';
import type { DeviceUsageInfo } from '@/types/device';
import Modal from '@/components/common/Modal';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import type { AssetCreateRequest, Asset } from '@/types/asset';

export default function AssetsPage() {
  const { isCustomerAdmin, user } = useAuth();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [pendingCredIds, setPendingCredIds] = useState<string[]>([]);
  const [page, setPage] = useState(0);

  const { data: assets, isLoading, error: assetsError } = useQuery({
    queryKey: coreApiMode ? ['assets',user?.tenant_id,page] : ['assets'],
    queryFn: () => getAssets(undefined,coreApiMode ? {skip:page*50,limit:50} : undefined),
  });

  const { data: credentials = [] } = useCredentials();
  const {data:count} = useQuery({queryKey:['assets','count',user?.tenant_id],queryFn:getAssetCount,enabled:coreApiMode});
  const {data:usage} = useQuery({queryKey:['usage','assets',user?.tenant_id],queryFn:getMyUsage,enabled:coreApiMode});

  const createMutation = useMutation({
    mutationFn: (payload: AssetCreateRequest) => createAsset(payload),
    onSuccess: async (asset) => {
      for (const credId of pendingCredIds) {
        await attachCredential(asset.id, credId);
      }
      setPendingCredIds([]);
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      queryClient.invalidateQueries({ queryKey: ['usage'] });
      setShowForm(false);
    },
  });

  function handleCreateSubmit(payload: AssetCreateRequest, credentialIds: string[]) {
    setPendingCredIds(credentialIds);
    createMutation.mutate(payload);
  }

  const deleteMutation = useMutation({
    mutationFn: (assetId: string) => deleteAsset(assetId),
    onSuccess: (_data,id) => { if (selectedAsset?.id === id) setSelectedAsset(null); queryClient.invalidateQueries({ queryKey: ['assets'] }); queryClient.invalidateQueries({ queryKey: ['usage'] }); },
  });

  function handleDelete(asset: Asset) {
    if (confirm(`Delete asset "${asset.label ?? asset.value}"? This cannot be undone.`)) {
      deleteMutation.mutate(asset.id);
    }
  }

  function handleRowClick(asset: Asset) {
    // Refresh the selected asset from the query cache so credentials are up-to-date
    const latest = (assets ?? []).find((a) => a.id === asset.id) ?? asset;
    setSelectedAsset(latest);
  }

  if (isLoading) return <LoadingSpinner label="Loading assets..." />;

  return (
    <div className="space-y-6">
      {(assetsError || createMutation.error || deleteMutation.error) && <p role="alert" className="rounded bg-red-50 p-3 text-red-700">{apiError(assetsError || createMutation.error || deleteMutation.error)}</p>}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Assets</h1>
        {isCustomerAdmin && (
          <button onClick={() => setShowForm(true)} className="btn-primary">
            Add Asset
          </button>
        )}
      </div>

      {usage?.device_limit != null && <div className="card"><DeviceUsage usage={usage as DeviceUsageInfo} /></div>}

      <AssetList
        assets={assets ?? []}
        onDelete={handleDelete}
        onRowClick={handleRowClick}
        canManage={isCustomerAdmin}
      />
      {coreApiMode && <div className="flex items-center justify-between text-sm text-gray-600">
        <span>{count?.total ?? '…'} assets (devices and discovery ranges) · Page {page+1}</span>
        <div className="flex gap-2">
          <button className="btn-secondary" disabled={page===0} onClick={()=>setPage(page-1)}>Previous</button>
          <button className="btn-secondary" disabled={!count || (page+1)*50>=count.total} onClick={()=>setPage(page+1)}>Next</button>
        </div>
      </div>}

      {/* Add Asset modal */}
      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title="Add Asset" size="lg">
        <AssetForm
          onSubmit={handleCreateSubmit}
          onCancel={() => setShowForm(false)}
          isSubmitting={createMutation.isPending}
          availableCredentials={credentials}
          credentialsEnabled={true}
        />
      </Modal>

      {/* Asset detail / credential management modal */}
      {selectedAsset && (
        <Modal
          isOpen={!!selectedAsset}
          onClose={() => setSelectedAsset(null)}
          title={selectedAsset.label ?? selectedAsset.value}
          size="md"
        >
          <div className="space-y-4">
            <div className="text-sm text-gray-600">
              <span className="font-medium">Type:</span>{' '}
              {selectedAsset.asset_type.replace('_', ' ')}
              {' · '}
              <span className="font-medium">Value:</span>{' '}
              <span className="font-mono">{selectedAsset.value}</span>
            </div>
            <AssetCredentialPanel
              asset={
                // Pull the latest version from cache (credentials may have changed)
                (assets ?? []).find((a) => a.id === selectedAsset.id) ?? selectedAsset
              }
            />
            {coreApiMode&&<AssetVerificationPanel key={selectedAsset.id} asset={(assets??[]).find(a=>a.id===selectedAsset.id)??selectedAsset} canManage={isCustomerAdmin}/>}
            {coreApiMode&&selectedAsset.asset_type==='ip_range'&&<DiscoveryPanel key={'discovery-'+selectedAsset.id} range={selectedAsset} canManage={isCustomerAdmin}/>}
          </div>
        </Modal>
      )}
    </div>
  );
}
